import { LocateFixed, Trash2, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  colorForUser,
  floorUnmapped,
  formatCoord,
  formatLevel,
  relativeTime,
  SOURCE_LABEL,
  sourceKind,
} from '@/lib/utils'
import type { LiveUser } from '@/types/liveview'

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-xs">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="truncate text-right font-medium tabular-nums">{value}</span>
    </div>
  )
}

export function UserDetail({
  user,
  now,
  following,
  onToggleFollow,
  onClose,
  onForget,
}: {
  user: LiveUser
  now: number
  following: boolean
  onToggleFollow: () => void
  onClose: () => void
  onForget: (userId: string) => void
}) {
  const kind = sourceKind(user.source)
  const unknownFloor = floorUnmapped(user.metadata)

  return (
    <div className="bg-card/95 border-t backdrop-blur">
      <div className="flex items-center gap-2 px-3 pt-3">
        <span
          className="size-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: colorForUser(user.user_id, user.source) }}
        />
        <h3 className="min-w-0 flex-1 truncate text-sm font-medium">
          {user.name ?? user.user_id}
        </h3>
        {kind !== 'phone' && (
          <Badge variant="outline" className="text-[10px]">
            {SOURCE_LABEL[kind]}
          </Badge>
        )}
        <Badge variant={user.online ? 'success' : 'secondary'} className="text-[10px]">
          {user.online ? 'online' : 'stale'}
        </Badge>
        <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close details">
          <X />
        </Button>
      </div>

      <Separator className="mt-3" />

      <div className="px-3 py-2">
        <Row label="User ID" value={<span className="font-mono text-[11px]">{user.user_id}</span>} />
        <Row label="Latitude" value={formatCoord(user.lat)} />
        <Row label="Longitude" value={formatCoord(user.lng)} />
        <Row
          label="Level"
          value={
            unknownFloor ? (
              // Not "—": the tag reported a floor, we simply could not place it.
              // BLUEIOT_GROUND_FLOOR_NO describes the engine, and this is what
              // getting it wrong looks like.
              <Badge variant="warning" className="px-1 py-0 text-[10px]">
                floor unknown
              </Badge>
            ) : (
              formatLevel(user.level)
            )
          }
        />
        <Row
          label="Accuracy"
          value={typeof user.accuracy === 'number' ? `±${user.accuracy.toFixed(2)} m` : '—'}
        />
        <Row label="Source" value={user.source ?? '—'} />
        {typeof user.heading === 'number' && <Row label="Heading" value={`${user.heading.toFixed(0)}°`} />}
        <Row label="Fix age" value={relativeTime(user.timestamp, now)} />
        <Row label="Updates" value={user.updates.toLocaleString()} />
        <Row label="First seen" value={relativeTime(user.first_seen, now)} />
        {user.background === true && <Row label="App state" value="background" />}
        {user.floor_id && (
          <Row label="Floor ID" value={<span className="font-mono text-[11px]">{user.floor_id}</span>} />
        )}
      </div>

      <div className="flex gap-2 px-3 pb-3">
        <Button
          variant={following ? 'default' : 'outline'}
          size="sm"
          className="flex-1"
          aria-pressed={following}
          onClick={onToggleFollow}
        >
          <LocateFixed />
          {following ? 'Following' : 'Follow'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive"
          aria-label="Forget this device"
          onClick={() => onForget(user.user_id)}
        >
          <Trash2 />
        </Button>
      </div>
    </div>
  )
}
